import DownloadIcon from '@mui/icons-material/Download';
import { Menu, MenuItem, Tooltip } from "@mui/material";
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from "react-markdown";
import AsystentAvatar from '../../assets/images/asystent_it_avatar.png';
import { useUser } from "../../core/UserContext";
import { useApi } from "../../hooks/UseApi";
import { useAutoScroll } from "../../hooks/useAutoScroll";
import { AssistantVariant, Message } from "../../models/models";
import { formatMessageTimestamp } from "../../utils/format-date";
import {
    AttachmentFooter,
    DocumentAttachmentWrapper,
    DocumentInfo,
    DocumentName,
    Dot,
    ErrorMessageSystemText,
    LoadingDots,
    MessagesContainer,
    MessageSystem,
    MessageSystemAuthor,
    MessageSystemHeader,
    MessageSystemHeaderIcons,
    MessageSystemText,
    MessageTimestamp,
    MessageYou,
    MessageYouAuthor,
    MessageYouText,
    StyledButton,
    StyledDownloadButton
} from './ChatMessages.styled';
import { MessageCitations } from './MessageCitations';

interface ChatMessagesProps {
    messages?: Message[];
    isGenerating?: boolean;

    onRetry?: () => void;
    onRetryNewChat?: () => void;
    canRetry?: boolean;

    variants?: AssistantVariant[];
    onActivateVariant?: (assistantMessageId: number) => void;
    canNavigateVariants?: boolean;
}

const ChatMessages = ({
                          messages,
                          isGenerating,
                          onRetry,
                          onRetryNewChat,
                          canRetry = true,
                          variants = [],
                          onActivateVariant,
                          canNavigateVariants = true
                      }: ChatMessagesProps) => {
    const {downloadDocumentForId} = useApi();
    const {user} = useUser();
    const {t} = useTranslation();
    const [retryAnchorEl, setRetryAnchorEl] = useState<null | HTMLElement>(null);
    const isRetryMenuOpen = Boolean(retryAnchorEl);
    const [openCitationIndex, setOpenCitationIndex] = useState<number | null>(null);

    const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
    const lastMessageText = lastMessage?.text || '';
    const lastMessageTextLength = lastMessageText.length;
    const isActuallyGenerating = !!(
        isGenerating &&
        lastMessage?.isSystem &&
        !lastMessage?.isLoading &&
        lastMessageTextLength > 0
    );

    const { containerRef } = useAutoScroll(messages, isGenerating, isActuallyGenerating);

    const openRetryMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!canRetry) return;
        setRetryAnchorEl(e.currentTarget);
    };
    const closeRetryMenu = () => setRetryAnchorEl(null);

    const toggleCitation = useCallback((index: number) => {
        setOpenCitationIndex(prevIndex => prevIndex === index ? null : index);
    }, []);

    const copyToClipboard = useCallback((text: string | React.ReactNode | (string | React.ReactNode)[]): void => {
        let textToCopy = '';
        if (Array.isArray(text)) {
            textToCopy = text.map(item =>
                typeof item === 'string'
                    ? item
                    : (item &&
                    typeof (item as any).props === 'object' &&
                    typeof (item as any).props.children === 'string'
                        ? (item as any).props.children
                        : '')
            ).join('');
        } else if (typeof text === 'string') {
            textToCopy = text;
        }
        if (textToCopy) {
            void navigator.clipboard.writeText(textToCopy);
        }
    }, []);

    if (messages?.length === 0) {
        return null;
    }

    return (
        <MessagesContainer ref={containerRef}>
            {messages?.map((message, index) => {
                const isLastAssistant = message.isSystem && index === (messages?.length ?? 1) - 1;
                const hasVariants =
                    isLastAssistant &&
                    !message.isLoading &&
                    !isGenerating &&
                    Array.isArray(variants) &&
                    variants.length > 0;

                const activeVariant = hasVariants
                    ? (variants.find(v => v.active) ?? variants[0])
                    : undefined;

                const displayedText =
                    (hasVariants && activeVariant?.content)
                        ? activeVariant.content
                        : message.text;

                const displayedDocs =
                    (hasVariants && activeVariant?.documents && activeVariant.documents.length > 0)
                        ? activeVariant.documents
                        : (message.documents || []);

                const displayedCitations =
                    (hasVariants && activeVariant?.citations)
                        ? activeVariant.citations
                        : (message.citations || []);

                const currentVariantIndex =
                    hasVariants && activeVariant
                        ? variants.findIndex(v => v.id === activeVariant.id)
                        : -1;

                const totalVariants = hasVariants ? variants.length : 0;

                const goPrevVariant = () => {
                    if (!hasVariants || !onActivateVariant || currentVariantIndex <= 0) return;
                    const prevId = variants[currentVariantIndex - 1].id;
                    onActivateVariant(prevId);
                };

                const goNextVariant = () => {
                    if (!hasVariants || !onActivateVariant || currentVariantIndex === -1 || currentVariantIndex >= totalVariants - 1) return;
                    const nextId = variants[currentVariantIndex + 1].id;
                    onActivateVariant(nextId);
                };

                return (
                    <div key={message.id} style={!message.isSystem ? {marginBottom: '0px'} : {}}>
                        {message.isSystem ? (
                            <>
                                <MessageSystem $hasAttachment={!!(displayedDocs && displayedDocs.length > 0)}>
                                    <MessageSystemHeader>
                                        <MessageSystemAuthor>
                                            <img src={AsystentAvatar} alt="Asystent IT"/>
                                        </MessageSystemAuthor>
                                        {!message.isLoading && !message.isError && (
                                            <MessageSystemHeaderIcons>
                                                <Tooltip title={t('chatMessages.copy')} arrow placement="top">
                                                    <StyledButton onClick={() => copyToClipboard(displayedText)}>
                                                        <i className="fa-regular fa-copy" style={{ color: '#6B7280', fontSize: '16px' }}></i>
                                                    </StyledButton>
                                                </Tooltip>
                                                {isLastAssistant && (
                                                    <>
                                                        <Tooltip title={t('chatMessages.retry')} arrow placement="top">
                                                            <span>
                                                                <StyledButton
                                                                    onClick={openRetryMenu}
                                                                    disabled={!canRetry}
                                                                    aria-haspopup="true"
                                                                    aria-expanded={isRetryMenuOpen ? 'true' : undefined}
                                                                    aria-controls={isRetryMenuOpen ? 'retry-menu' : undefined}
                                                                >
                                                                    <i className="fa-solid fa-rotate-right" style={{ color: '#6B7280', fontSize: '16px' }}></i>
                                                                </StyledButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Menu
                                                            id="retry-menu"
                                                            anchorEl={retryAnchorEl}
                                                            open={isRetryMenuOpen}
                                                            onClose={closeRetryMenu}
                                                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                                        >
                                                            <MenuItem
                                                                onClick={() => {
                                                                    closeRetryMenu();
                                                                    onRetry?.();
                                                                }}
                                                            >
                                                                {t('chatMessages.retrySame')}
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    closeRetryMenu();
                                                                    onRetryNewChat?.();
                                                                }}
                                                            >
                                                                {t('chatMessages.retryNewChat')}
                                                            </MenuItem>
                                                        </Menu>
                                                    </>
                                                )}
                                            </MessageSystemHeaderIcons>
                                        )}
                                    </MessageSystemHeader>
                                    {message.isLoading ? (
                                        <LoadingDots>
                                            <Dot/>
                                            <Dot/>
                                            <Dot/>
                                        </LoadingDots>
                                    ) : message.isError ? (
                                        <ErrorMessageSystemText>{message.text}</ErrorMessageSystemText>
                                    ) : (
                                        <>
                                            <MessageSystemText>
                                                <ReactMarkdown>
                                                    {(displayedText || '').replace(
                                                        /<thinking>([\s\S]*?)<\/thinking>/g,
                                                        (_m, p1) =>
                                                            `**${t('chatMessages.deepReasoning')}:** _${p1}_`
                                                    )}
                                                </ReactMarkdown>
                                            </MessageSystemText>
                                            <MessageCitations
                                                citations={displayedCitations || []}
                                                openIndex={openCitationIndex}
                                                onToggle={toggleCitation}
                                            />
                                        </>
                                    )}
                                </MessageSystem>

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        marginBottom: '32px'
                                    }}
                                >
                                    {hasVariants && totalVariants > 1 && (
                                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                            <Tooltip title={t('chatMessages.prevVariant')} arrow placement="top">
                                                <span>
                                                    <StyledButton
                                                        onClick={goPrevVariant}
                                                        disabled={!canNavigateVariants || currentVariantIndex <= 0}
                                                        aria-label={t('chatMessages.prevVariant') || ''}
                                                    >
                                                        <i className="fa-solid fa-chevron-left" style={{ color: '#1d2a41', fontSize: '14px' }}></i>
                                                    </StyledButton>
                                                </span>
                                            </Tooltip>
                                            <span
                                                style={{color: '#1d2a41', fontSize: 12}}
                                                title={t('chatMessages.variantCounter') || ''}
                                                aria-label={t('chatMessages.variantCounter') || ''}
                                            >
                                                {(currentVariantIndex + 1)}/{totalVariants}
                                            </span>
                                            <Tooltip title={t('chatMessages.nextVariant')} arrow placement="top">
                                                <span>
                                                    <StyledButton
                                                        onClick={goNextVariant}
                                                        disabled={
                                                            !canNavigateVariants ||
                                                            currentVariantIndex === -1 ||
                                                            currentVariantIndex >= totalVariants - 1
                                                        }
                                                        aria-label={t('chatMessages.nextVariant') || ''}
                                                    >
                                                        <i className="fa-solid fa-chevron-right" style={{ color: '#1d2a41', fontSize: '14px' }}></i>
                                                    </StyledButton>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}

                                    {displayedDocs && displayedDocs.length > 0 ? (
                                        <>
                                            {displayedDocs.map((doc, docIndex) => (
                                                <DocumentAttachmentWrapper key={`doc-${docIndex}`} $show={true}>
                                                    <AttachmentFooter>
                                                        <DocumentInfo>
                                                            <DocumentName>
                                                                {t('chatMessages.source') + doc.documentName}
                                                            </DocumentName>
                                                        </DocumentInfo>
                                                        {(doc.documentId && doc.documentId !== -1) && (
                                                            <Tooltip
                                                                title={t('chatMessages.download')}
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <StyledDownloadButton
                                                                    variant="text"
                                                                    color="primary"
                                                                    size="small"
                                                                    startIcon={<DownloadIcon/>}
                                                                    onClick={() => {
                                                                        if (doc.documentId && doc.documentId !== -1 && doc.documentName) {
                                                                            downloadDocumentForId(
                                                                                doc.documentId,
                                                                                doc.documentName
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </AttachmentFooter>
                                                </DocumentAttachmentWrapper>
                                            ))}
                                        </>
                                    ) : null}
                                    {message.timestamp && !message.isLoading && !message.isError && (
                                        <MessageTimestamp
                                            style={
                                                !(displayedDocs && displayedDocs.length > 0)
                                                    ? {marginLeft: 'auto'}
                                                    : {}
                                            }
                                        >
                                            {formatMessageTimestamp(message.timestamp)}
                                        </MessageTimestamp>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <MessageYou $hasAttachment={!!(message.documents && message.documents.length > 0)}>
                                    <MessageYouAuthor>
                                        {user?.avatarBase64 ? (
                                            <img
                                                style={{
                                                    width: '35px',
                                                    height: '35px',
                                                    borderRadius: '50%',
                                                }}
                                                src={user.avatarBase64}
                                                alt="User Avatar"
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: '35px',
                                                    height: '35px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#1976d2',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {(() => {
                                                    const firstName = user?.firstName || user?.name || '';
                                                    const lastName = user?.lastName || user?.surname || '';
                                                    if (firstName && lastName) {
                                                        return `${firstName.charAt(0)}${lastName.charAt(0)}`;
                                                    } else if (firstName) {
                                                        return firstName.charAt(0);
                                                    } else if (user?.email) {
                                                        return user.email.charAt(0).toUpperCase();
                                                    } else {
                                                        return '?';
                                                    }
                                                })()}
                                            </div>
                                        )}
                                        <span>
                                            {(() => {
                                                const firstName = user?.firstName || user?.name || '';
                                                const lastName = user?.lastName || user?.surname || '';

                                                if (firstName && lastName) {
                                                    return `${firstName} ${lastName}`;
                                                } else if (firstName) {
                                                    return firstName;
                                                } else if (lastName) {
                                                    return lastName;
                                                } else {
                                                    return t('chatMessages.system');
                                                }
                                            })()}
                                        </span>
                                    </MessageYouAuthor>
                                    <MessageYouText>{message.text}</MessageYouText>
                                </MessageYou>

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        marginBottom: '32px'
                                    }}
                                >
                                    {message.documents && message.documents.length > 0 ? (
                                        <>
                                            {message.documents.map((doc, docIndex) => (
                                                <DocumentAttachmentWrapper key={`doc-${docIndex}`} $show={true}>
                                                    <AttachmentFooter>
                                                        <DocumentInfo>
                                                            <DocumentName>{doc.documentName}</DocumentName>
                                                        </DocumentInfo>
                                                        {(doc.documentId && doc.documentId !== -1) && (
                                                            <Tooltip
                                                                title={t('chatMessages.download')}
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <StyledDownloadButton
                                                                    variant="text"
                                                                    color="primary"
                                                                    size="small"
                                                                    startIcon={<DownloadIcon/>}
                                                                    onClick={() => {
                                                                        if (doc.documentId && doc.documentId !== -1 && doc.documentName) {
                                                                            downloadDocumentForId(
                                                                                doc.documentId,
                                                                                doc.documentName
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </AttachmentFooter>
                                                </DocumentAttachmentWrapper>
                                            ))}
                                        </>
                                    ) : null}
                                    {message.timestamp && (
                                        <MessageTimestamp
                                            style={
                                                !(message.documents && message.documents.length > 0)
                                                    ? {marginLeft: 'auto'}
                                                    : {}
                                            }
                                        >
                                            {formatMessageTimestamp(message.timestamp)}
                                        </MessageTimestamp>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </MessagesContainer>
    );
};

export default ChatMessages;
